// EP-135 Tier 3 — Customer-side meeting + ballot hooks.
//
// Mirrors `api/app/controllers/api/customer_app/{meetings,votes}_controller.rb`.
// Customer-app endpoints are gated on the `condominium_enabled` account
// setting; the cast_via:'self' path stamps nothing (no `recorded_by_id`).
// The cast URL mirrors the operator-side so the controllers diverge only
// at the authorization + stamping layer.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  CustomerMeetingSummary,
  CustomerMeetingDetail,
  CustomerMinutesDocument,
  VoteChoice,
} from '@/types/meetings'

export const meetingKeys = {
  all: ['customer', 'meetings'] as const,
  detail: (id: number) => [ ...meetingKeys.all, id ] as const,
  minutes: (id: number) => [ ...meetingKeys.detail(id), 'minutes' ] as const,
}

export function useMeetings() {
  return useQuery({
    queryKey: meetingKeys.all,
    queryFn: async () => {
      const response = await api.get<{ data: CustomerMeetingSummary[] }>(
        '/api/customer/meetings',
      )
      return response.data.data
    },
  })
}

export function useMeeting(id: number) {
  return useQuery({
    queryKey: meetingKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<{ data: CustomerMeetingDetail }>(
        `/api/customer/meetings/${id}`,
      )
      return response.data.data
    },
    enabled: !!id,
  })
}

// EP-135 Tier 4 — Customer-side read-only minutes hook.
// The server returns 422 minutes_not_yet_certified when the meeting is
// not yet `certified`, so this hook is enabled by the caller's
// `meetingStatus` (only fetch once the meeting is certified).
export function useMeetingMinutes(
  id: number,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: meetingKeys.minutes(id),
    queryFn: async () => {
      const response = await api.get<{ data: CustomerMinutesDocument }>(
        `/api/customer/meetings/${id}/minutes`,
      )
      return response.data.data
    },
    enabled: options.enabled !== false && !!id,
  })
}

interface CastVoteInput {
  resolutionId: number
  attendeeId: number
  choice: VoteChoice
}

interface CastVoteResponse {
  id: number
  choice: VoteChoice
  cast_at: string
  receipt_hash: string
}

export function useCastVote(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ resolutionId, attendeeId, choice }: CastVoteInput) => {
      const response = await api.post<{ data: CastVoteResponse }>(
        `/api/customer/meetings/${meetingId}/resolutions/${resolutionId}/votes`,
        { vote: { attendee_id: attendeeId, choice } },
      )
      return response.data.data
    },
    onSuccess: () => {
      // The meeting detail carries per-resolution own_vote + tally; a
      // successful cast changes both, so a single invalidation covers it.
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) })
      queryClient.invalidateQueries({ queryKey: meetingKeys.all })
    },
  })
}
